import {Button, Stack, Flash} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {StopIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useConvertToDraftMutation} from '../../hooks/mutations/use-convert-to-draft-mutation'
import {useAnalytics} from '@github-ui/use-analytics'

export function ConvertToDraftSection() {
  const [dialogState, setDialogState] = useState<'closed' | 'open'>('closed')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const convertToDraftRef = useRef<HTMLButtonElement>(null)
  const confirmConvertToDraftRef = useRef<HTMLButtonElement>(null)

  const {sendAnalyticsEvent} = useAnalytics()

  const onOpenConvertToDraft = () => {
    setDialogState('open')
    setErrorMessage(null)
  }

  const {mutate: convertToDraftMutation, isPending} = useConvertToDraftMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      setTimeout(() => announce('Failed to convert the pull request to draft'), 1000)
    },
  })

  const handleConvertToDraft = async () => {
    if (isPending) return
    setErrorMessage(null)

    convertToDraftMutation(undefined, {
      onSuccess: () => {
        setDialogState('closed')
        setTimeout(() => announce('The pull request was successfully converted to draft.'), 1000)
        sendAnalyticsEvent(
          'convert_to_draft_section.convert_to_draft',
          'MERGEBOX_CONVERT_TO_DRAFT_SECTION_CONVERT_TO_DRAFT_BUTTON',
        )
      },
    })
  }

  return (
    <>
      <Stack direction="horizontal" align="baseline" className="py-2 px-1 text-small flex-justify-end" gap="none">
        <span className="fgColor-muted pr-1">Still in progress?</span>
        <Button
          variant="link"
          className="Link--muted Link--inTextBlock text-normal"
          onClick={onOpenConvertToDraft}
          ref={convertToDraftRef}
        >
          Convert to draft
        </Button>
      </Stack>

      <Dialog
        aria-labelledby="convert-to-draft-dialog-title"
        isOpen={dialogState === 'open'}
        returnFocusRef={convertToDraftRef}
        initialFocusRef={confirmConvertToDraftRef}
        onDismiss={() => setDialogState('closed')}
      >
        <Dialog.Header id="convert-to-draft-dialog-title">Convert this pull request to draft?</Dialog.Header>
        <div className="p-3">
          <Flash variant="warning">People who are already subscribed will not be unsubscribed.</Flash>

          {errorMessage && (
            <Flash className="my-2" variant="danger">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}

          <div className="d-flex flex-justify-end mt-3">
            <Button
              className="mr-1"
              disabled={isPending}
              variant="invisible"
              onClick={() => {
                if (!isPending) {
                  setDialogState('closed')
                  convertToDraftRef.current?.focus()
                }
              }}
            >
              Cancel
            </Button>
            <Button
              loading={isPending}
              loadingAnnouncement="Converting to draft"
              onClick={handleConvertToDraft}
              ref={confirmConvertToDraftRef}
            >
              Convert to draft
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
